import type { ProductSizeOption, ShippingFeeSettings } from '@repo/api-contracts';
import {
  addDestinationError,
  blankToNull,
  checkQuoteRequestDestinationRules,
  DESTINATION_LIST_MESSAGE,
  type DestinationFieldErrors,
  destinationErrorEntry,
  destinationListMessage,
  type MutableDestinationErrors,
  parseQuoteQuantity,
  QUOTE_REQUEST_INVALID_INPUT_MESSAGE,
  QUOTE_REQUEST_SHARED_FIELD_KEYS,
  type QuotePriceTier,
  type QuoteRequestRuleContext,
  type QuoteRequestSharedFieldKey,
  quoteRequestFieldMessage,
  SIZE_OPTIONS_CHANGED_MESSAGE,
  type SizeFieldValues,
  type SizeQuantityRow,
} from '@/entities/quote';
import {
  firstErrorPerField,
  getErrorMessage,
  getServerFieldErrors,
  type ServerFieldErrors,
} from '@/shared/lib';
import type { DestinationDraft } from './use-destination-list';

// 회원 견적 요청 제출의 공용 검증·칸 오류 매핑 — 디자인 견적(`useSubmitQuoteRequest`)과 일반 견적(`useSubmitPlainQuoteRequest`)이
// 함께 쓴다. 두 요청은 디자인·인쇄 방식 칸만 다르고 배송지·납기일·메모·담당자 칸과 규칙은 같다.

export interface ShippingAddressValues {
  readonly recipientName: string;
  readonly recipientPhone: string;
  readonly postalCode: string;
  readonly address1: string;
  readonly address2: string;
}

/** 회원 배송지 카드 — 배송지 다섯 칸 + 이 배송지로 받을 수량(입력창 문자열). */
export type MemberDestinationValues = ShippingAddressValues & {
  readonly quantity: string;
  readonly sizes: readonly SizeQuantityRow[];
};

/** 두 요청이 함께 받는 폼 입력 — 입력창 문자열 그대로이고 검증은 safeParse 가 한다. 견적 수량은 배송지 수량 합계다. */
export interface QuoteRequestCommonValues {
  readonly sizeFields: SizeFieldValues;
  readonly desiredDate: string;
  readonly destinations: readonly DestinationDraft<MemberDestinationValues>[];
  readonly memo: string;
  readonly companyName: string;
  readonly contactName: string;
}

/** 칸 오류 — `quantity` 는 총 수량(합계 규칙), `destinations` 는 배송지 묶음 자체(개수 등) 오류다. */
export type QuoteRequestFieldKey = QuoteRequestSharedFieldKey | 'destinations';

export type QuoteRequestFieldErrors = Partial<Record<QuoteRequestFieldKey, string>>;

/** 카드 key → 그 카드의 칸 오류. */
export type QuoteRequestDestinationErrors = Readonly<Record<string, DestinationFieldErrors>>;

export interface UseSubmitQuoteRequestOptions {
  readonly sizeOptions: readonly ProductSizeOption[];
  readonly minOrderQty: number;
  /** 임시 견적과 같은 구간 단가 — 총 수량 × 단가 + 배송비가 견적 금액 상한을 넘는지 미리 본다. */
  readonly priceTiers: readonly QuotePriceTier[];
  /** 요청 폼 조회의 배송비 설정 — 임시 견적과 같은 배송비로 금액 상한을 본다(제출 때는 서버가 다시 읽는다). */
  readonly shippingFeeSettings: ShippingFeeSettings;
  /** 희망 납기일 "한국 내일" 판정 기준 시각. 기본은 실제 시계, 테스트는 고정 시계를 넣는다. */
  readonly now?: () => Date;
}

export interface UseSubmitQuoteRequestResult<TValues> {
  readonly fieldErrors: QuoteRequestFieldErrors;
  readonly destinationErrors: QuoteRequestDestinationErrors;
  readonly formError: string | null;
  readonly isPending: boolean;
  /** 성공하면 견적 요청 id, 검증·서버 거절이면 null. */
  readonly submit: (values: TValues) => Promise<string | null>;
}

export interface QuoteRequestSubmissionErrors {
  readonly fieldErrors: QuoteRequestFieldErrors;
  readonly destinationErrors: QuoteRequestDestinationErrors;
  readonly formError: string | null;
}

/** 계약 스키마 safeParse 결과 — 디자인·일반 두 스키마의 결과를 같은 모양으로 받는다. */
export type QuoteRequestParseResult<TInput> =
  | { readonly success: true; readonly data: TInput }
  | {
      readonly success: false;
      readonly error: {
        readonly issues: readonly {
          readonly path: readonly PropertyKey[];
          readonly code: string;
          readonly message?: string;
        }[];
      };
    };

export interface QuoteRequestValidation<TInput> extends QuoteRequestSubmissionErrors {
  readonly input: TInput | null;
}

const FORM_ERROR_FALLBACK = '견적 요청에 실패했습니다.';

export const realClock = (): Date => new Date();

/** 공용 칸을 계약 입력 모양으로 — 빈 선택 항목은 null, 수량은 정수 아니면 NaN(스키마가 거부). */
export const toQuoteRequestCommonInput = (values: QuoteRequestCommonValues) => ({
  desiredDate: blankToNull(values.desiredDate),
  destinations: values.destinations.map(({ value: { quantity, sizes, ...address } }) => ({
    ...address,
    quantity: parseQuoteQuantity(quantity),
    ...(sizes.length > 0
      ? {
          sizes: sizes.map((row) => ({
            sizeOptionId: row.sizeOptionId,
            quantity: parseQuoteQuantity(row.quantity),
          })),
        }
      : {}),
  })),
  memo: values.memo,
  companyName: values.companyName,
  contactName: values.contactName,
});

const isFieldIn = (
  key: PropertyKey,
  fieldKeys: readonly QuoteRequestSharedFieldKey[],
): key is QuoteRequestSharedFieldKey =>
  typeof key === 'string' && (fieldKeys as readonly string[]).includes(key);

const hasAnyError = (errors: QuoteRequestSubmissionErrors): boolean =>
  Object.keys(errors.fieldErrors).length > 0 ||
  Object.keys(errors.destinationErrors).length > 0 ||
  errors.formError !== null;

/**
 * safeParse 결과 + 스키마 밖 규칙 → 칸 오류. `fieldKeys` 는 그 요청이 화면에 보여 주는 공용 칸이다 — 목록에 없는 칸(디자인 id·상품 id 등)의
 * 스키마 오류는 일반 폼 오류다.
 */
export const validateQuoteRequest = <TInput>(
  parsed: QuoteRequestParseResult<TInput>,
  values: QuoteRequestCommonValues,
  context: QuoteRequestRuleContext,
  shippingFeeSettings: ShippingFeeSettings,
  fieldKeys: readonly QuoteRequestSharedFieldKey[] = QUOTE_REQUEST_SHARED_FIELD_KEYS,
  sizeOptions: readonly ProductSizeOption[] = [],
): QuoteRequestValidation<TInput> => {
  const schemaErrors: QuoteRequestFieldErrors = {};
  const destinationErrors: MutableDestinationErrors = {};
  let formError: string | null = null;

  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const [head] = issue.path;
      const entry = destinationErrorEntry(
        issue.path,
        issue.code,
        values.destinations,
        issue.message,
      );
      if (entry !== null) {
        // 사이즈 상품의 수량은 파생값이다. 빈 줄·줄 수량 오류는 보이는 sizes 칸에서 안내한다.
        if (sizeOptions.length > 0 && entry.field === 'quantity') continue;
        addDestinationError(destinationErrors, entry.cardKey, entry.field, entry.message);
      } else if (head === 'destinations') {
        schemaErrors.destinations ??= destinationListMessage(issue.code);
      } else if (head !== undefined && isFieldIn(head, fieldKeys)) {
        schemaErrors[head] ??= quoteRequestFieldMessage(head, issue.code);
      } else {
        formError = QUOTE_REQUEST_INVALID_INPUT_MESSAGE;
      }
    }
  }

  const rules = checkQuoteRequestDestinationRules(
    values,
    sizeOptions,
    shippingFeeSettings,
    context,
  );
  const fieldErrors: QuoteRequestFieldErrors = { ...rules.totalErrors, ...schemaErrors };
  if (rules.sizeTotalError !== null) fieldErrors.quantity = rules.sizeTotalError;
  if (rules.allocationError !== null) fieldErrors.destinations ??= rules.allocationError;
  for (const [cardKey, message] of Object.entries(rules.destinationSizeErrors)) {
    addDestinationError(destinationErrors, cardKey, 'sizes', message);
  }

  const errors = { fieldErrors, destinationErrors, formError };
  return { ...errors, input: hasAnyError(errors) || !parsed.success ? null : parsed.data };
};

/**
 * 서버 필드 오류 → 칸. 계약 검증(flatten)의 배송지 오류는 `destinations` 한 키로 오고 배송지 묶음 오류로 보여 준다. 서버 도메인 검사
 * (`INVALID_DESTINATIONS`)는 점으로 이은 한 마디 키(`destinations.1.postalCode`, 교차 조정 R3)로 오고, 제출한 카드 순서로 그 카드
 * key 를 찾아 우리 문구로 붙인다. 그 밖에 칸으로 못 보내는 키(designId·없는 카드 등)는 zod 원문 대신 일반 폼 오류로 바꾼다.
 */
const mapServerFieldErrors = (
  errors: ServerFieldErrors,
  values: QuoteRequestCommonValues,
  fieldKeys: readonly QuoteRequestSharedFieldKey[],
): QuoteRequestSubmissionErrors => {
  const fieldErrors: QuoteRequestFieldErrors = firstErrorPerField(errors, fieldKeys);
  const destinationErrors: MutableDestinationErrors = {};
  let formError: string | null = null;

  for (const key of Object.keys(errors)) {
    if (key === 'destinations') {
      fieldErrors.destinations = DESTINATION_LIST_MESSAGE;
      continue;
    }
    if (isFieldIn(key, fieldKeys)) continue;
    const entry = destinationErrorEntry(key, 'server', values.destinations);
    if (entry === null) formError = QUOTE_REQUEST_INVALID_INPUT_MESSAGE;
    else if (entry.field === 'sizes') {
      // 로컬 검사를 통과한 사이즈를 서버가 거절했다 — 폼을 연 뒤 사이즈 옵션이 바뀌었다. 사이즈 줄이 숨은 폼에서도 보이게
      // 배송지 묶음에도 안내한다.
      addDestinationError(destinationErrors, entry.cardKey, 'sizes', SIZE_OPTIONS_CHANGED_MESSAGE);
      fieldErrors.destinations ??= SIZE_OPTIONS_CHANGED_MESSAGE;
    } else addDestinationError(destinationErrors, entry.cardKey, entry.field, entry.message);
  }

  return { fieldErrors, destinationErrors, formError };
};

/**
 * 제출 거절 → 화면 오류. 칸으로 보낼 서버 필드 오류가 있으면 그 칸에, 아니면(준비 중·요청 불가 사유·수량 등) 서버 문구를 폼 오류로
 * 보여 준다 — 오류 형태에 도메인 코드가 없어 사유는 문구로만 온다(최종 리뷰 T6 ruling).
 */
export const quoteRequestSubmitErrors = (
  error: unknown,
  values: QuoteRequestCommonValues,
  fieldKeys: readonly QuoteRequestSharedFieldKey[] = QUOTE_REQUEST_SHARED_FIELD_KEYS,
): QuoteRequestSubmissionErrors => {
  const serverFieldErrors = getServerFieldErrors(error);
  const mapped =
    serverFieldErrors === null ? null : mapServerFieldErrors(serverFieldErrors, values, fieldKeys);
  if (mapped !== null && hasAnyError(mapped)) return mapped;
  return {
    fieldErrors: {},
    destinationErrors: {},
    formError: getErrorMessage(error, FORM_ERROR_FALLBACK),
  };
};
